import { colors, fonts, layout, spacing } from '../theme';

const baseStyles = {
  fontFamily: fonts.heading,
  fontSize: '1rem',
  fontWeight: 500,
  padding: '0.5rem 1rem',
  borderRadius: layout.borderRadius,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacing.sm,
  minHeight: '44px', // Touch-friendly
  minWidth: '44px',
  textDecoration: 'none',
  backgroundImage: 'none',
};

const variants = {
  primary: {
    backgroundColor: colors.blue,
    color: colors.white,
    '&:hover:not(:disabled)': {
      backgroundColor: colors.darkBlue,
    },
  },
  secondary: {
    backgroundColor: colors.green,
    color: colors.white,
    '&:hover:not(:disabled)': {
      backgroundColor: '#527a24',
    },
  },
  danger: {
    backgroundColor: colors.red,
    color: colors.white,
    '&:hover:not(:disabled)': {
      backgroundColor: '#c43f2a',
    },
  },
  outline: {
    backgroundColor: 'transparent',
    color: colors.blue,
    border: `1px solid ${colors.blue}`,
    '&:hover:not(:disabled)': {
      backgroundColor: colors.gray50,
    },
  },
  ghost: {
    backgroundColor: 'transparent',
    color: colors.midGray,
    '&:hover:not(:disabled)': {
      backgroundColor: colors.gray100,
      color: colors.darkGray,
    },
  },
};

const sizes = {
  sm: {
    fontSize: '0.875rem',
    padding: '0.375rem 0.75rem',
    minHeight: '32px',
  },
  md: {
    fontSize: '1rem',
    padding: '0.5rem 1rem',
    minHeight: '44px',
  },
  lg: {
    fontSize: '1.125rem',
    padding: '0.75rem 1.5rem',
    minHeight: '48px',
  },
};

function Button({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  onClick,
  type = 'button',
  fullWidth = false,
  style = {},
  className = '',
  'aria-label': ariaLabel,
  ...props
}) {
  const variantStyle = variants[variant] || variants.primary;
  const sizeStyle = sizes[size] || sizes.md;

  const buttonStyle = {
    ...baseStyles,
    ...sizeStyle,
    ...variantStyle,
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.6 : 1,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    ...style,
  };

  // Handle hover styles (simplified - actual hover handled by CSS)
  const handleMouseEnter = (e) => {
    if (!disabled && !loading && variantStyle['&:hover:not(:disabled)']) {
      const hoverStyle = variantStyle['&:hover:not(:disabled)'];
      Object.keys(hoverStyle).forEach((key) => {
        if (key !== '&:hover:not(:disabled)') {
          e.target.style[key] = hoverStyle[key];
        }
      });
    }
  };

  const handleMouseLeave = (e) => {
    if (!disabled && !loading) {
      Object.keys(variantStyle).forEach((key) => {
        if (key !== '&:hover:not(:disabled)') {
          e.target.style[key] = variantStyle[key];
        }
      });
    }
  };

  const iconElement = icon && (
    <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
  );

  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      style={buttonStyle}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={ariaLabel}
      aria-busy={loading}
      {...props}
    >
      {loading && (
        <span
          style={{
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: `2px solid ${colors.gray200}`,
            borderTop: `2px solid ${variant === 'outline' || variant === 'ghost' ? colors.blue : colors.white}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginRight: iconPosition === 'left' && icon ? spacing.xs : 0,
          }}
          aria-hidden="true"
        />
      )}
      {!loading && iconPosition === 'left' && iconElement}
      {children}
      {!loading && iconPosition === 'right' && iconElement}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
}

export default Button;

